import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, parseISO } from 'date-fns';
import type {
    DatasetComponentOption,
    GridComponentOption,
    BarSeriesOption,
    LineSeriesOption,
    TitleComponentOption,
    TooltipComponentOption,
    LegendComponentOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | BarSeriesOption
    | LineSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
    | DatasetComponentOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    BarChart,
    LineChart,
    LegendComponent,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface FunnelBucket {
    period: string;
    period_start: string;
    visits?: number;
    wishlist?: number;
    registered?: number;
    inquiries?: number;
    orders?: number;
}

interface Props {
    data: FunnelBucket[];
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
}

const FunnelTrendChart: React.FC<Props> = ({ data, period }) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!data || data.length === 0) {
            if (chartInstance.current && chartRef.current) {
                chartInstance.current.setOption({ series: [] });
            }
            return;
        }

        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            const categories: string[] = [];
            const visits: number[] = [];
            const wishlist: number[] = [];
            const registered: number[] = [];
            const inquiries: number[] = [];
            const orders: number[] = [];

            data.forEach(item => {
                let date: Date;
                try {
                    date = item.period_start ? parseISO(item.period_start) : new Date();
                    if (isNaN(date.getTime())) {
                        date = new Date(item.period_start);
                    }
                } catch {
                    date = new Date();
                }

                let label = '';
                switch (period) {
                    case 'daily':
                        label = format(date, 'MMM d');
                        break;
                    case 'weekly':
                        label = `W${format(date, 'II')} ${format(date, 'yyyy')}`;
                        break;
                    case 'monthly':
                        label = format(date, 'MMM yyyy');
                        break;
                    case 'quarterly':
                        label = `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
                        break;
                    default:
                        label = item.period || '';
                }

                categories.push(label);
                visits.push(Number(item.visits) || 0);
                wishlist.push(Number(item.wishlist) || 0);
                registered.push(Number(item.registered) || 0);
                inquiries.push(Number(item.inquiries) || 0);
                orders.push(Number(item.orders) || 0);
            });

            const option: ECOption = {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                },
                legend: {
                    data: ['Visits', 'Wishlist', 'Registered', 'Inquiry', 'Orders'],
                    bottom: 0,
                },
                grid: {
                    left: '6%',
                    right: '6%',
                    bottom: 60,
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: categories,
                    axisLabel: { rotate: period === 'daily' ? 45 : 0 },
                },
                yAxis: {
                    type: 'value',
                    name: 'Distinct Visitors',
                },
                series: [
                    {
                        name: 'Visits',
                        type: 'bar',
                        data: visits,
                        itemStyle: { color: '#1890ff' },
                        barMaxWidth: 30,
                    },
                    {
                        name: 'Wishlist',
                        type: 'bar',
                        data: wishlist,
                        itemStyle: { color: '#13c2c2' },
                        barMaxWidth: 30,
                    },
                    {
                        name: 'Registered',
                        type: 'bar',
                        data: registered,
                        itemStyle: { color: '#52c41a' },
                        barMaxWidth: 30,
                    },
                    {
                        name: 'Inquiry',
                        type: 'bar',
                        data: inquiries,
                        itemStyle: { color: '#722ed1' },
                        barMaxWidth: 30,
                    },
                    {
                        name: 'Orders',
                        type: 'bar',
                        data: orders,
                        itemStyle: { color: '#fa8c16' },
                        barMaxWidth: 30,
                    },
                ],
            };

            chartInstance.current.setOption(option);
        }

        const resizeHandler = () => chartInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [data, period]);

    return <div ref={chartRef} style={{ width: '100%', height: 360 }} />;
};

export default FunnelTrendChart;

